import { Fragment, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
    isDirectMiddleClick,
    preventMiddleClickAutoscroll,
} from '@/utils/mouse';
import {
    TableColumn,
    InlineEditConfig,
    TableEmptyStateConfig,
} from '@/types/table';
import { TableCell } from './TableCell';
import { TableRowSkeleton, SkeletonType } from './TableRowSkeleton';
import { TableEmptyState } from './TableEmptyState';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface TableBodyProps<TData> {
    data: TData[];
    columns: TableColumn<TData>[];
    isLoading: boolean;
    onRowClick?: (row: TData, event?: MouseEvent<HTMLTableRowElement>) => void;
    page?: number;
    inlineEditConfig?: InlineEditConfig<TData>;
    skeletonRowCount?: number;
    skeletonTypeMap?: Record<string, SkeletonType>;
    emptyState?: TableEmptyStateConfig;
    expandableRow?: (row: TData, isExpanded: boolean) => React.ReactNode;
    expandedKey?: string | null;
    onToggleExpand?: (key: string) => void;
}

const EmptyState = ({
    columnCount,
    message,
}: {
    columnCount: number;
    message: string;
}) => (
    <tr>
        <td colSpan={columnCount} className="h-32 text-center text-grey-300">
            {message}
        </td>
    </tr>
);

const getColumnStyles = <T,>(column: TableColumn<T>): React.CSSProperties => ({
    ...(column.width && { width: column.width }),
    ...(column.minWidth && { minWidth: column.minWidth }),
    ...(column.maxWidth && { maxWidth: column.maxWidth }),
});

function renderCellContent<TData>(
    column: TableColumn<TData>,
    row: TData,
    value: unknown,
    isExpanded?: boolean,
    toggleExpand?: () => void,
): React.ReactNode {
    if (column.cell) {
        return column.cell({
            row: { original: row },
            value,
            isExpanded,
            toggleExpand,
        });
    }
    if (column.accessor) {
        return String(value ?? '');
    }
    return null;
}

export function TableBody<TData>({
    data,
    columns,
    isLoading,
    onRowClick,
    page = 1,
    inlineEditConfig,
    skeletonRowCount = 5,
    skeletonTypeMap,
    emptyState,
    expandableRow,
    expandedKey,
    onToggleExpand,
}: TableBodyProps<TData>) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const columnCount = columns.length;

    if (isLoading) {
        return (
            <TableRowSkeleton
                columns={columns}
                rowCount={skeletonRowCount}
                skeletonTypeMap={skeletonTypeMap}
            />
        );
    }

    if (data.length === 0) {
        if (emptyState) {
            return (
                <TableEmptyState
                    config={emptyState}
                    columnCount={columnCount}
                />
            );
        }

        return (
            <tbody>
                <EmptyState
                    columnCount={columnCount}
                    message={t('table.noRecords')}
                />
            </tbody>
        );
    }

    const rowClassName = cn(
        'border-b border-grey-500 transition-colors hover:bg-surface-hover',
        onRowClick && 'cursor-pointer',
    );

    return (
        <tbody>
            {data.map((row, rowIndex) => {
                const rowKey = `${page}-${rowIndex}`;
                const rowId = inlineEditConfig?.getRowId(row) ?? rowKey;
                const isExpanded = !!expandableRow && expandedKey === rowKey;
                const toggleExpand = expandableRow
                    ? () => onToggleExpand?.(rowKey)
                    : undefined;

                return (
                    <Fragment key={rowKey}>
                        <tr
                            onClick={
                                onRowClick
                                    ? (event) => onRowClick(row, event)
                                    : undefined
                            }
                            onAuxClick={
                                onRowClick
                                    ? (event) => {
                                          if (isDirectMiddleClick(event))
                                              onRowClick(row, event);
                                      }
                                    : undefined
                            }
                            onMouseDown={
                                onRowClick
                                    ? preventMiddleClickAutoscroll
                                    : undefined
                            }
                            className={rowClassName}
                        >
                            {columns.map((column, colIndex) => (
                                <TableCell
                                    key={colIndex}
                                    column={column}
                                    row={row}
                                    rowId={rowId}
                                    style={getColumnStyles(column)}
                                    inlineEditConfig={inlineEditConfig}
                                    renderContent={(value) =>
                                        renderCellContent(
                                            column,
                                            row,
                                            value,
                                            isExpanded,
                                            toggleExpand,
                                        )
                                    }
                                />
                            ))}
                        </tr>
                        {expandableRow && (
                            <tr>
                                <td colSpan={columnCount} className="p-0">
                                    <div
                                        className={cn(
                                            'grid transition-[grid-template-rows] duration-200 ease-out',
                                            isExpanded
                                                ? 'grid-rows-[1fr]'
                                                : 'grid-rows-[0fr]',
                                        )}
                                    >
                                        <div className="overflow-hidden">
                                            <div
                                                className={cn(
                                                    'px-4 py-3 bg-grey-650/30 transition-[border-width] duration-200',
                                                    isExpanded
                                                        ? 'border-b border-grey-500'
                                                        : 'border-b-0',
                                                )}
                                            >
                                                {expandableRow(row, isExpanded)}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </Fragment>
                );
            })}
        </tbody>
    );
}
